import { supabase } from '../lib/supabase';

/**
 * Follow Service - Maneja las operaciones de seguir/dejar de seguir
 * Usa directamente Supabase con las credenciales del usuario autenticado
 */

export const followService = {
  /**
   * Seguir a un usuario
   * @param {string} followerId - ID del usuario que sigue
   * @param {string} followingId - ID del usuario a seguir
   */
  async followUser(followerId, followingId) {
    if (followerId === followingId) {
      throw new Error('No puedes seguirte a ti mismo');
    }

    // Verificar si ya sigue
    const { data: existing } = await supabase
      .from('follows')
      .select('*')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .single();

    if (existing) {
      return { success: true, message: 'Ya sigues a este chef', is_following: true };
    }

    // Crear el follow
    const { error } = await supabase
      .from('follows')
      .insert({
        follower_id: followerId,
        following_id: followingId
      });

    if (error) {
      console.error('Error following user:', error);
      throw new Error(error.message || 'Error al seguir');
    }

    return { success: true, message: '¡Ahora sigues a este chef!', is_following: true };
  },

  /**
   * Dejar de seguir a un usuario
   * @param {string} followerId - ID del usuario que deja de seguir
   * @param {string} followingId - ID del usuario a dejar de seguir
   */
  async unfollowUser(followerId, followingId) {
    const { error } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', followerId)
      .eq('following_id', followingId);

    if (error) {
      console.error('Error unfollowing user:', error);
      throw new Error(error.message || 'Error al dejar de seguir');
    }

    return { success: true, message: 'Has dejado de seguir a este chef', is_following: false };
  },

  /**
   * Verificar si un usuario sigue a otro
   * @param {string} followerId - ID del usuario que sigue
   * @param {string} followingId - ID del usuario seguido
   */
  async checkFollowStatus(followerId, followingId) {
    if (!followerId || !followingId) return false;
    
    const { data } = await supabase
      .from('follows')
      .select('*')
      .eq('follower_id', followerId)
      .eq('following_id', followingId)
      .single();

    return !!data;
  },

  /**
   * Obtener la lista de seguidores de un usuario
   * @param {string} userId - ID del usuario
   */
  async getFollowers(userId) {
    const { data, error } = await supabase
      .from('follows')
      .select(`
        follower_id,
        profiles!follows_follower_id_fkey (
          id,
          display_name,
          username,
          avatar_url,
          followers_count
        )
      `)
      .eq('following_id', userId);

    if (error) {
      console.error('Error getting followers:', error);
      return [];
    }

    return data?.map(f => f.profiles).filter(Boolean) || [];
  },

  /**
   * Obtener la lista de usuarios que sigue un usuario
   * @param {string} userId - ID del usuario
   */
  async getFollowing(userId) {
    const { data, error } = await supabase
      .from('follows')
      .select(`
        following_id,
        profiles!follows_following_id_fkey (
          id,
          display_name,
          username,
          avatar_url,
          followers_count
        )
      `)
      .eq('follower_id', userId);

    if (error) {
      console.error('Error getting following:', error);
      return [];
    }

    return data?.map(f => f.profiles).filter(Boolean) || [];
  },

  /**
   * Obtener perfil público de un usuario con contadores
   * @param {string} userId - ID del usuario
   */
  async getPublicProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error getting profile:', error);
      return null;
    }

    // Contar recetas públicas
    const { count: recipesCount } = await supabase
      .from('recipes')
      .select('*', { count: 'exact', head: true })
      .eq('author_id', userId)
      .eq('is_public', true);

    return {
      ...data,
      recipes_count: recipesCount || 0
    };
  },

  /**
   * Buscar chefs por nombre
   * @param {string} query - Término de búsqueda
   * @param {number} limit - Límite de resultados
   */
  async searchChefs(query = '', limit = 20) {
    let queryBuilder = supabase
      .from('profiles')
      .select('*')
      .limit(limit);

    if (query) {
      queryBuilder = queryBuilder.or(`display_name.ilike.%${query}%,username.ilike.%${query}%`);
    } else {
      // Si no hay query, ordenar por seguidores
      queryBuilder = queryBuilder.order('followers_count', { ascending: false });
    }

    const { data, error } = await queryBuilder;

    if (error) {
      console.error('Error searching chefs:', error);
      return [];
    }

    // Obtener conteo de recetas para cada chef
    const chefsWithRecipes = await Promise.all(
      (data || []).map(async (profile) => {
        const { count } = await supabase
          .from('recipes')
          .select('*', { count: 'exact', head: true })
          .eq('author_id', profile.id)
          .eq('is_public', true);

        return {
          ...profile,
          recipes_count: count || 0
        };
      })
    );

    return chefsWithRecipes;
  }
};

export default followService;
